// The strategist's own asks. Mounted on GET /api/requests/mine, not the book
// cache: a 694KB payload with a five-minute staleTime is why a just-queued
// request used to vanish until reload. Approvers already have the Requests band.

import type { BookAccount, BookPayload, MyRequest } from '../api/types';
import { SectionHead } from '../components/SectionHead.tsx';
import { formatCount } from '../lib/format.ts';
import { bankAgeLabel, scoutRead } from './bank.ts';
import {
  accountFromRequest, couldntReasonLine, declinedForViewer, isApprovedRequest, isCouldntRequest,
  isPendingRequest,
} from './bankRequest.ts';
import { RequestFlagNotes } from './RequestFlagNotes.tsx';
import { closedRequestsLabel, splitRequests } from './requestSplit.ts';

function rowFor(request: MyRequest, book: BookPayload | null): BookAccount {
  const existing = book?.accounts.find(account => account.id === request.account_id);
  return existing ?? accountFromRequest(request.account_id, request);
}

function RequestMark({
  request,
  viewerEmail,
}: {
  request: MyRequest;
  viewerEmail: string;
}) {
  if (isPendingRequest(request)) {
    return (
      <span className="requestmark" title="Requested — waiting for approval">
        Requested ✓
      </span>
    );
  }
  if (isApprovedRequest(request)) {
    return (
      <span className="requestmark" title="Approved">
        Approved ✓
      </span>
    );
  }
  if (isCouldntRequest(request)) {
    const line = couldntReasonLine(request.declined_reason);
    return (
      <div className="requestcouldnt" title={line}>
        {line}
      </div>
    );
  }

  const declined = declinedForViewer(request, viewerEmail);
  if (!declined) return null;
  const reason = declined.declined_reason || null;
  return (
    <div className="requestdeclined" title={reason || 'Declined'}>
      Declined{reason ? ` — ${reason}` : ''}
    </div>
  );
}

function RequestsTable({
  requests,
  book,
  viewerEmail,
  today,
  onOpen,
  label,
}: {
  requests: MyRequest[];
  book: BookPayload | null;
  viewerEmail: string;
  today: Date;
  onOpen: (accountId: string) => void;
  label: string;
}) {
  return (
    <div className="tablewrap" tabIndex={0} role="region" aria-label={label}>
      <table className="banktable">
        <colgroup>
          <col className="bankaccountcol" />
          <col className="bankfitcol" />
          <col />
          <col className="bankactioncol" />
        </colgroup>
        <thead>
          <tr>
            <th>Account</th>
            <th>Scout’s read</th>
            <th>Scout updated</th>
            <th><span className="visually-hidden">Request</span></th>
          </tr>
        </thead>
        <tbody>
          {requests.map(request => {
            const row = rowFor(request, book);
            return (
              <tr className="acct" key={`${request.account_id}:${request.requested_by}`}>
                <td>
                  <button type="button" className="briefline" onClick={() => onOpen(row.id)}>
                    <span className="briefacct">{row.account}</span>
                    <span className="briefwhy">{row.domain}</span>
                  </button>
                  <RequestFlagNotes request={request} />
                </td>
                <td>{scoutRead(row) || '—'}</td>
                <td>{book ? bankAgeLabel(row, today) : '—'}</td>
                <td>
                  <RequestMark request={request} viewerEmail={viewerEmail} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function MyRequests({
  requests,
  book,
  viewerEmail,
  today,
  onOpen,
}: {
  requests: MyRequest[];
  book: BookPayload | null;
  viewerEmail: string;
  today: Date;
  onOpen: (accountId: string) => void;
}) {
  const { pending, closed } = splitRequests(requests);
  const tableProps = { book, viewerEmail, today, onOpen };

  return (
    <section className="myrequests" aria-label="My requests">
      <SectionHead
        title="My requests"
        count={`${formatCount(pending.length)} pending`}
      />
      <p className="briefempty">
        Waiting on admin or leadership approval. Once they approve, the account
        moves onto your book without waiting for the Snowflake mirror.
      </p>
      {/* Closed requests (approved, declined, couldn't be requested) sit collapsed above the
          pending table, so the section opens on what still needs an answer. */}
      {closed.length > 0 ? (
        <details className="closedrequests">
          <summary>{closedRequestsLabel(closed.length)}</summary>
          <RequestsTable requests={closed} label="Closed requests" {...tableProps} />
        </details>
      ) : null}
      {pending.length === 0 ? (
        <p className="briefempty">You have no pending requests.</p>
      ) : (
        <RequestsTable requests={pending} label="My requests" {...tableProps} />
      )}
    </section>
  );
}
